import { Component, Input, Output, OnInit, AfterViewInit, OnChanges,  forwardRef, SimpleChanges, EventEmitter } from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, 
	NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { isString, isUndefined, isNull, isEmpty, uniqueId } from 'lodash';

const isNullOrDefined = (obj : any) => {
	return null == obj || isUndefined(obj) || isEmpty(obj);
}

const validKeys = [
	"ArrowLeft", "ArrowRight", "Backspace", "Delete"	
];

@Component({
	selector: 'app-custom-date-widget',
	templateUrl: './custom-date-widget.component.html',
	styleUrls: ['./custom-date-widget.component.scss'],
	providers: [
		{
		  provide : NG_VALUE_ACCESSOR,
		  multi : true,
		  useExisting : forwardRef(() => CustomDateWidgetComponent)
		}
	]
})
export class CustomDateWidgetComponent extends FormGroup implements OnInit, OnChanges, AfterViewInit
{
	@Input() label : string;
	@Input() dateGroupName : string;
	@Input() required : boolean = false;
	@Input() inputDate : string | Date
	@Output() dateChanged : EventEmitter<string | Date | undefined> = new EventEmitter<string | Date | undefined>();

	public id : string = uniqueId().toString();
	private day : number | undefined;
	private month : number | undefined;
	private year : number | undefined;
	

	private onChanged = (obj : any) => {};
	private onTouched = () => {};
	
	private formGroup : FormGroup;
	private dateGroup : FormGroup;
	override errors : null | { invalidDate : boolean };
	private pattern = /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/;		
	@Input() showValidationErrorIfAllInputHaveValues : boolean;

	constructor(private controlContainer : ControlContainer,
		private fb : FormBuilder)
	{		
		super([], []);
	}

	ngOnInit(): void {
		this.createControls();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if(changes["inputDate"] && 
			new RegExp(this.pattern, "i").test(changes["inputDate"].currentValue()))
		{
			const parts = changes["inputDate"].currentValue?.split("/");			
			if(this.dateDay)
			{
				this.dateDay.setValue(parts[0]?.toString().padStart(2, "0"));
			}
			if(this.dateMonth)
			{
				this.dateMonth.setValue(parts[1]?.toString().padStart(2, "0"));
			}

			if(this.dateYear)
			{
				this.dateYear.setValue(parts[2]?.toString().padStart(2, "0"));
			}
		}
	}
	
	ngAfterViewInit(): void {
		//this.createControls();
	}
	

	private createControls = () => {
		this.formGroup = <FormGroup>this.controlContainer.control;
		this.dateGroup = this.fb.group({
			dateDay : ["",  { validators : [Validators.maxLength(2), Validators.min(1), Validators.max(31), this.dayValidator] } ],
			dateMonth : ["",  { validators : [Validators.maxLength(2), Validators.min(1), Validators.max(12)] } ],
			dateYear : ["",  { validators : [Validators.minLength(4), Validators.maxLength(4), Validators.min(1900), Validators.max(9999)] } ]
		});
		
		this.dateDay?.valueChanges?.subscribe({
			next : (data) => {
				if(!!data)
				{
					this.dateMonth?.addValidators(Validators.required);
					this.dateYear?.addValidators(Validators.required);
				}
				else
				{
					this.dateMonth?.removeValidators(Validators.required);
					this.dateYear?.removeValidators(Validators.required);
				}
			}
		});

		this.dateMonth?.valueChanges?.subscribe({
			next : (data) => {
				if(!!data)
				{
					this.dateDay?.addValidators(Validators.required);
					this.dateYear?.addValidators(Validators.required);
				}
				else
				{
					this.dateDay?.removeValidators(Validators.required);
					this.dateYear?.removeValidators(Validators.required);
				}
			}
		});

		this.dateYear?.valueChanges?.subscribe({
			next : (data) => {
				if(!!data)
				{
					this.dateDay?.addValidators(Validators.required);
					this.dateMonth?.addValidators(Validators.required);
				}
				else
				{
					this.dateDay?.removeValidators(Validators.required);
					this.dateMonth?.removeValidators(Validators.required);					
				}
			}
		})


		if(this.required)
		{
			this.dateGroup.addValidators(Validators.required);
		}

		this.formGroup?.addControl(this.dateGroupName, this.dateGroup);		
	}

	getFormControlName = (name: string) => {		
		return this.dateGroup?.get(name);		
	}

	
	get dateDay() 
	{
		return this.getFormControl("dateDay") as FormControl;
	}

	get dateMonth() 
	{
		return this.getFormControl("dateMonth") as FormControl;
	}

	get dateYear() 
	{
		return this.getFormControl("dateYear") as FormControl;
	}

	public getFormControl = (name : string) : any => 
	{
		//return this.formGroup?.get(name);

		return this.dateGroup?.get(name) as FormControl;	
	}

	dayValidator = () : ValidatorFn => {
		return (formControl : AbstractControl) : ValidationErrors | null => {
			
			const month = this.dateMonth?.value;
			const year = this.dateYear?.value;
			const [min, max] = this.getDayMinMax(month, year);			
			const valid : boolean = this.checkDateValidity((<FormControl>formControl)?.value, 
				this.dateMonth?.value, this.dateYear?.value);			

			return valid ? null : { error: "Invalid date" };
		}
	}
	
	
	private checkDateValidity = (day : string, month : string, year : string) => {		
		const iMonth = /^\d+$/i.test(month?.toString()) ? Number(month) : undefined;
		const iYear = /^\d+$/i.test(year?.toString()) ? Number(year) : undefined;
		const iDay = /^\d+$/i.test(day?.toString()) ? Number(day) : undefined;

		const hasAllValues = !!iDay && !!iMonth && !!iYear;		
		const allNulls = !iDay && !iMonth && !iYear;
		let isValid = allNulls;
		
		
		if(hasAllValues)
		{
			const yearValid = this.dateYear?.valid;
			const monthValid = this.dateMonth?.valid;			
			const [min, max] = this.getDayMinMax(iMonth!.toString(), iYear!.toString());	
			isValid = yearValid && monthValid && iDay! >= min && iDay! <= max;
		}		
		if(!isValid)
		{	
			this.errors = { invalidDate : true };
		}
		
		return isValid;
	}

	private getDayMinMax = (month : string, year : string) => {
		let max = 30;
		const min = 1;
		const iMonth = /^\d+$/i.test(month?.toString()) ? Number(month) : new Date().getMonth() + 1;		
		const iYear = /^\d+$/i.test(year?.toString()) ? Number(year) : new Date().getFullYear();
		
		if( 0 !==  iMonth % 2 || 8 === iMonth || 10 === iMonth || 12 === iMonth)
		{
			max = 31;
		}
		else
		{
			if(2 === iMonth)
			{					
				max = (0 === iYear % 4 || (0 === iYear % 100 && 0 === iYear % 400)) ? 29 : 28;									
			}					
			else
			{
				max = 30;
			}
		}				

		return [min, max];
	}

	private adjustDayValidators = (control : FormControl, month : string, year : string) =>
	{
		if(control && /^\d+$/i.test(month?.toString()))
		{
			const [min, max] = this.getDayMinMax(month, year); 
			control.clearValidators();					
			control.addValidators([Validators.maxLength(2), Validators.min(min), Validators.max(max)]);
		}
	}	


	private getPreviewText = (element : HTMLInputElement, inText : string) => {
		const startPos = element.selectionStart, endPos = element.selectionEnd;
		
		const texts : string[] = [];

		if(0 === startPos)
		{
			texts.push(inText);
			texts.push(element.value.substring(endPos!));
		}
		else if(0 < startPos!)
		{				
			texts.push(element.value.substring(0, startPos!));
			texts.push(inText);
			texts.push(element.value.substring(endPos!));
		}

		return texts.filter(Boolean).join("");
	}

	onDateChanged = (event : any) => {
		const valid = this.isValid();
		// if(!valid)
		// {
		// 	this.errors = { invalidDate : true };
		// 	this.dateDay?.setErrors(this.errors);
		// }
		// else
		// {
		// 	this.errors = null;
		// 	this.dateDay?.setErrors( { invalidDate : false });
		// }
		this.dateChanged.emit(this.getDateEntered());
	}

	public onKeyPress = (event : any) => {
		const element = event.target ?? event.currentTarget;		

		if(!(/^\d+$/.test(event.key) || -1 < validKeys.indexOf(event.key)))
		{
			event.preventDefault();
			return;
		}
		const maxLength = Number(element.getAttribute("maxLength"));

		const value = this.getPreviewText(element, event.key)?.toString();
		if(value)
		{			
			const min = element.getAttribute("min");
			const max = element.getAttribute("max");
			if(value.length === maxLength && (Number(value) < min || Number(value) > max))
			{
				event.preventDefault();
				return;
			}
		}

		//this.dateChanged.emit(this.getDateEntered());
	}

	public onDayKeyPress = (event : any, formControlName : string) => {
		
		const formControl = this.getFormControl(formControlName);
		
		const element = event.target ?? event.currentTarget;		

		if(!(/^\d+$/.test(event.key) || -1 < validKeys.indexOf(event.key)))
		{
			event.preventDefault();
			return;
		}

		// if((formControl && !formControl.valid) || !element?.validity?.valid)
		// {
		// 	event.preventDefault();
		// 	return;
		// }		
		
		const value = this.getPreviewText(element, event.key);
		const month = this.dateMonth?.value;
		const year = this.dateYear?.value;
		const day = this.dateDay?.value;		

		const [min, max] = this.getDayMinMax(month, year); 
		const maxLength = Number(element.getAttribute("maxLength"));

		if(value.length === maxLength && (Number(value) < min || Number(value) > max))
		{			
			event.preventDefault();
			return;
		}

		//this.dateChanged.emit(this.getDateEntered());		
	}
	
	private getDateEntered() 
	{		
		const iMonth = /^\d+$/i.test(this.dateMonth?.value?.toString()) ? Number(this.dateMonth?.value?.toString()) : undefined;
		const iYear = /^\d+$/i.test(this.dateYear?.value?.toString()) ? Number(this.dateYear?.value?.toString()) : undefined;
		const iDay = /^\d+$/i.test(this.dateDay?.value?.toString()) ? Number(this.dateDay?.value?.toString()) : undefined;
		
		const allNulls = !iDay && !iMonth && !iYear;
		if(allNulls)
		{
			return undefined;
		}
		
		const validDate = this.checkDateValidity(this.dateDay?.value, this.dateMonth?.value, this.dateYear?.value);
		if(validDate)
		{			
			return new Date(Number(iYear), Number(iMonth) - 1, Number(iDay)).toISOString();
		}

		return undefined;
	}

	isValid = () => {
		const isValid = this.checkDateValidity(this.dateDay?.value, this.dateMonth?.value, this.dateYear?.value);
		return isValid;
	}

	getErrors = () => {
		return this.errors;
	}

	override reset = () => 
	{
		if(this.dateDay)
		{
			this.dateDay.reset();
		}
		if(this.dateMonth)
		{
			this.dateMonth.reset();
		}

		if(this.dateYear)
		{
			this.dateYear.reset();
		}
	}	

	override get dirty() {
		return this.dateDay?.dirty || this.dateMonth?.dirty || this.dateYear?.dirty;
	}

	
	override readonly value = this.getDateEntered();

	override patchValue (value : any, options? : { onlySelf? : boolean | undefined;	emitEvent ? : boolean | undefined;}) 
	{
		const parts = new RegExp(this.pattern, "i").test(value) ? value?.split("/") : ["", "", ""];		
		if(this.dateDay)
		{
			this.dateDay.patchValue(/^\d+$/i.test(parts[0]) ? parts[0]?.toString().padStart(2, "0") : "", options);
		}
		if(this.dateMonth)
		{	
			this.dateMonth.patchValue(/^\d+$/i.test(parts[1]) ? parts[1]?.toString().padStart(2, "0") : "", options);
		}

		if(this.dateYear)
		{
			this.dateYear.patchValue(/^\d+$/i.test(parts[2]) ? parts[2]?.toString().padStart(4, "0") : "", options);				
		}
	}

	override setValue (value : any, options? : { onlySelf? : boolean | undefined;	emitEvent ? : boolean | undefined;}) 
	{
		const parts = new RegExp(this.pattern, "i").test(value) ? value?.split("/") : ["", "", ""];		
		if(this.dateDay)
		{
			this.dateDay.setValue(/^\d+$/i.test(parts[0]) ? parts[0]?.toString().padStart(2, "0") : "", options);
		}
		if(this.dateMonth)
		{
			this.dateMonth.setValue(/^\d+$/i.test(parts[1]) ? parts[1]?.toString().padStart(2, "0") : "", options);
		}

		if(this.dateYear)
		{
			this.dateYear.setValue(/^\d+$/i.test(parts[2]) ? parts[2]?.toString().padStart(4, "0") : "", options);				
		}		
	}

	getValue() {
		return this.getDateEntered();
	}
}
